<script setup lang="ts">
import { computed } from 'vue';

import type { IFormSelectInputOption } from '@modules/forms/types';
import VDropdownMenu from '@components/dropdowns/VDropdownMenu/VDropdownMenu.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useDashboardFilters, useDashboardPeriodOptions } from '@modules/dashboard/composables';
import { useDashboardMerchantsQuery } from '@modules/dashboard/queries';
import { useDashboardStore } from '@modules/dashboard/store';
import { watchImmediate } from '@vueuse/core';

const { data: merchantsList, isLoading } = useDashboardMerchantsQuery();

const { setPeriod, setMerchant } = useDashboardStore();
const { merchantId, period } = useDashboardFilters();
const { periodOptions } = useDashboardPeriodOptions();

const merchantOptions = computed<IFormSelectInputOption<number>[]>(
  () => merchantsList.value?.map((v) => ({ value: v.id, label: v.value || v.merchant_name })) ?? [],
);

function onSetMerchant(id?: number): void {
  const merchant = merchantsList.value?.find((item) => item.id === id);
  if (merchant) {
    setMerchant(merchant);
  }
}

watchImmediate(merchantsList, (newValue) => {
  if (newValue?.length && !merchantId.value) {
    setMerchant(newValue[0]);
  }
}, {
  deep: true,
});
</script>

<template>
  <VLoader v-data-qa-auto-id="'TheDashboardMerchants'"
    :loading="isLoading"
    spinner-size="small"
    min-height="39px"
    min-width="180px"
  >
    <span class="flex w-full justify-between gap-2 sm:w-auto sm:justify-normal">
      <VDropdownMenu
        name="merchantId"
        :options="merchantOptions"
        :value="merchantId"
        underlined
        style-type="dark"
        @change="onSetMerchant"
      />
      <VDropdownMenu
        name="period"
        :options="periodOptions"
        :value="period"
        underlined
        style-type="dark"
        @change="setPeriod"
      />
    </span>
  </VLoader>
</template>
